/**
 * Locale-aware date/time formatting utilities.
 *
 * All formatters accept a BCP 47 locale string (e.g. "de-DE", "en-US")
 * and an IANA timezone (e.g. "Europe/Berlin").
 *
 * Derive the locale from merchant fields with getMerchantLocale().
 */

/**
 * Build a BCP 47 locale tag from merchant settings.
 * Falls back to 'en-US' when data is missing.
 */
export function getMerchantLocale(
  defaultLanguage?: string | null,
  countryCode?: string | null
): string {
  if (defaultLanguage && countryCode) {
    return `${defaultLanguage}-${countryCode}`;
  }
  if (defaultLanguage) {
    return defaultLanguage;
  }
  return 'en-US';
}

// ---------------------------------------------------------------------------
// Core formatters
// ---------------------------------------------------------------------------

/**
 * Full date + time: "Fr., 28.02.2026, 18:00" (de-DE) / "Fri, 02/28/2026, 6:00 PM" (en-US)
 */
export function formatDateTime(
  date: Date | string,
  locale: string,
  timezone: string
): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString(locale, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  });
}

/**
 * Date only: "28.02.2026" (de-DE) / "02/28/2026" (en-US)
 */
export function formatDate(
  date: Date | string,
  locale: string,
  timezone: string
): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: timezone,
  });
}

/**
 * Long date: "Freitag, 28. Februar 2026" (de-DE) / "Friday, February 28, 2026" (en-US)
 */
export function formatDateLong(
  date: Date | string,
  locale: string,
  timezone: string
): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: timezone,
  });
}

/**
 * Short date with month name: "28. Feb. 2026" (de-DE) / "Feb 28, 2026" (en-US)
 */
export function formatDateShort(
  date: Date | string,
  locale: string,
  timezone: string
): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: timezone,
  });
}

/**
 * Time only: "18:00" (de-DE) / "6:00 PM" (en-US)
 */
export function formatTime(
  date: Date | string,
  locale: string,
  timezone: string
): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  });
}

/**
 * Date + time without weekday: "28.02.2026, 18:00" (de-DE) / "02/28/2026, 6:00 PM" (en-US)
 */
export function formatDateTimeCompact(
  date: Date | string,
  locale: string,
  timezone: string
): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  });
}

/**
 * Month + year only: "Februar 2026" (de-DE) / "February 2026" (en-US)
 */
export function formatMonthYear(
  date: Date | string,
  locale: string,
  timezone: string
): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: timezone,
  });
}
